// Client-side "AI" engine for the Σύμβουλος Σχολών.
// No external API: everything below is deterministic math over the historical
// βάσεις (2018–2026) the app already loads from Supabase. We extrapolate each
// department's next-year βάση from its trend, then score how well the user's
// μόρια fit — the same reach / match / safety idea used for college lists.

/**
 * Least-squares linear regression over [{ year, score }] points, evaluated at
 * targetYear. Falls back gracefully for 0/1 points. The result is clamped near
 * the observed range so a steep short trend can't produce an absurd number.
 */
export function predictScore(points, targetYear) {
  const pts = (points || [])
    .filter((p) => Number.isFinite(p.year) && Number.isFinite(p.score))
    .sort((a, b) => a.year - b.year)

  if (pts.length === 0) return null
  const scores = pts.map((p) => p.score)
  const last = scores[scores.length - 1]
  if (pts.length === 1) return last

  const n = pts.length
  const sx = pts.reduce((s, p) => s + p.year, 0)
  const sy = pts.reduce((s, p) => s + p.score, 0)
  const sxx = pts.reduce((s, p) => s + p.year * p.year, 0)
  const sxy = pts.reduce((s, p) => s + p.year * p.score, 0)
  const denom = n * sxx - sx * sx
  if (denom === 0) return last

  const slope = (n * sxy - sx * sy) / denom
  const intercept = (sy - slope * sx) / n
  const raw = slope * targetYear + intercept

  // Weight the pure trend against the last known value so single-year spikes
  // don't dominate, then clamp within a sane band around observed scores.
  const blended = 0.6 * raw + 0.4 * last
  const min = Math.min(...scores) - 600
  const max = Math.max(...scores) + 900
  return Math.round(Math.min(max, Math.max(min, blended)))
}

/**
 * Logistic admission probability: 50% exactly at the (predicted) βάση, rising
 * above it and falling below. `spread` controls how quickly it changes.
 */
export function admissionProbability(userScore, predicted, spread = 380) {
  if (!Number.isFinite(userScore) || !Number.isFinite(predicted)) return 0
  const p = 1 / (1 + Math.exp(-(userScore - predicted) / spread))
  return Math.min(0.98, Math.max(0.02, p))
}

export const BUCKETS = {
  safe: {
    key: 'safe',
    label: 'Ασφαλείς επιλογές',
    hint: 'Τα μόριά σου καλύπτουν άνετα την εκτιμώμενη βάση.',
  },
  match: {
    key: 'match',
    label: 'Καλές πιθανότητες',
    hint: 'Είσαι κοντά ή πάνω από την εκτιμώμενη βάση.',
  },
  reach: {
    key: 'reach',
    label: 'Φιλόδοξοι στόχοι',
    hint: 'Οριακά κάτω από τη βάση — αξίζει να το παλέψεις.',
  },
}

export function bucketFor(prob) {
  if (prob >= 0.8) return 'safe'
  if (prob >= 0.5) return 'match'
  if (prob >= 0.28) return 'reach'
  return null // too unlikely to recommend
}

/**
 * Turn raw score rows into ranked recommendations.
 * @param {Array} rows - [{ year, score, department: {id,name,university,city,slug} }]
 * @param {number} userScore
 * @param {number} targetYear
 */
export function buildRecommendations(rows, userScore, targetYear) {
  const byDept = new Map()
  for (const row of rows) {
    const d = row.department
    if (!d?.id) continue
    if (!byDept.has(d.id)) byDept.set(d.id, { department: d, points: [] })
    byDept.get(d.id).points.push({ year: row.year, score: row.score })
  }

  const items = []
  for (const { department, points } of byDept.values()) {
    const predicted = predictScore(points, targetYear)
    if (predicted == null) continue
    const prob = admissionProbability(userScore, predicted)
    const bucket = bucketFor(prob)
    if (!bucket) continue

    const latest = [...points].sort((a, b) => b.year - a.year)[0]
    items.push({
      department,
      predicted,
      lastScore: latest?.score ?? null,
      lastYear: latest?.year ?? null,
      history: points.sort((a, b) => a.year - b.year),
      prob,
      match: Math.round(prob * 100),
      bucket,
    })
  }

  // Within each bucket, show the strongest match first. Ties break by the more
  // competitive (higher βάση) department.
  const grouped = { safe: [], match: [], reach: [] }
  for (const item of items) grouped[item.bucket].push(item)
  for (const key of Object.keys(grouped)) {
    grouped[key].sort((a, b) => b.prob - a.prob || b.predicted - a.predicted)
  }
  return grouped
}
